import {
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { TileTabProps } from "../TileTabSelector";
import { DestinationWindow } from "client/Api/Apps/common";
import { AfasLiveInformationProvider } from "client/Features/Workspace/Tiles/Afas/provider";
import { isNil, isNotNil } from "client/Misc/utils";
import { AfasResourceKind } from "client/Api/Workspace/LiveInformation/Afas/afasLiveInformation";
import { MySignals } from "./MySignals";
import { MyTasks } from "./MyTasks";
import { navigateToAfasApp } from "client_legacy/Apps/appNavigation";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { AfasTabDisplayModel } from "client/Api/Workspace/workspace";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type AfasLiveTileContentProps = {
	connectionId: number;
	tabs: AfasTabDisplayModel[];
	destinationWindow: DestinationWindow;
	provider: AfasLiveInformationProvider;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const AfasLiveTileContent = (props: AfasLiveTileContentProps): ReactElement => {
	const {
		destinationWindow,
		connectionId,
		provider,
		tabs,
		onOpenTileMenu
	} = props;

	const {
		dimension,
		icon,
		id,
		name,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(tabs[0]?.uniqueId);

	const userTimezone: TimeZone = useUserTimezone();

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const activeTab: AfasTabDisplayModel | undefined = tabs.find(x => x.uniqueId === activeTabId);

	const tabProps = useMemo(
		(): readonly TileTabProps[] => {
			return tabs.map(x => {
				const result: TileTabProps = {
					uniqueId: x.uniqueId,
					name: x.name
				};

				return result;
			});
		},
		[tabs]
	);

	const navigationHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			navigateToAfasApp({
				id: environmentAppId,
				destinationWindow
			});
		},
		[
			destinationWindow,
			environmentAppId,
			addAnalyticsEvent
		]
	);

	const renderTabContent = (): ReactElement | null => {
		if (isNil(activeTab)) {
			return null;
		}

		switch(activeTab.resourceKind) {
			case AfasResourceKind.MySignals:
				return (
					<MySignals
						tabId={activeTab.uniqueId}
						provider={provider}
						connectionId={connectionId}
						onNavigate={navigationHandler}
						timezone={userTimezone}
					/>
				);
			case AfasResourceKind.MyTasks:
				return (
					<MyTasks
						dimensionToRender={dimension}
						destinationWindow={destinationWindow}
						provider={provider}
						connectionId={connectionId}
						tabId={activeTab.uniqueId}
						timezone={userTimezone}
						onListItemClick={addAnalyticsEvent}
					/>
				);
			default:
				never(activeTab.resourceKind);
		}
	};

	return (
		<LiveTileContent
			icon={icon}
			name={name}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.TabSelector
					tileId={id}
					tileName={name}
					value={activeTabId}
					onChange={setActiveTabId}
					tabs={tabProps}
				/>
			</LiveTileContent.Header>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{renderTabContent()}
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>
			{isNotNil(activeTab) && (
				<LiveTileContent.Footer
					name={getFooterText(activeTab.resourceKind)}
					onClick={navigationHandler}
				/>
			)}
		</LiveTileContent>
	);
};

AfasLiveTileContent.displayName = "AfasLiveTileContent";

function getFooterText(resourceKind: AfasResourceKind): string {
	switch(resourceKind) {
		case AfasResourceKind.MySignals:
			return localize("_MySignals");
		case AfasResourceKind.MyTasks:
			return localize("_MyTasks");
		default:
			never(resourceKind);
	}
}
